// useActiveBridge — session-persisted active-bridge selection for the
// multi-bridge selector row (Multi-Bridge UI).
//
// Responsibilities:
//   - Restore the active bridge from sessionStorage on load; if unset or the
//     stored bridge is no longer in the list, default to the first CONNECTED
//     bridge (else the first bridge).
//   - Persist every explicit selection (sessionStorage key wb_active_bridge_id).
//   - NEVER auto-switch when the active bridge merely disconnects — it stays
//     selected (its card shows an error state) until the operator picks another.
//   - Signal the server which bridge's camera the browser is watching
//     (POST /wb/live/camera-preview) on the active-bridge edges, so the agent
//     only encodes frames for the watched bridge. Pauses the previous bridge
//     and on unmount.

import { useCallback, useEffect, useRef, useState } from 'react';

import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import type { LiveBridge } from './useWbLiveBridges';

const STORAGE_KEY = 'wb_active_bridge_id';

function readStored(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeStored(id: number | null): void {
  try {
    if (id === null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, String(id));
  } catch {
    /* sessionStorage unavailable (private mode / SSR) — selection is in-memory only */
  }
}

function signalCameraPreview(weighbridgeId: number, active: boolean): void {
  void apiClient
    .post(endpoints.wb.liveCameraPreview, { weighbridge_id: weighbridgeId, active })
    .catch(() => undefined);
}

export interface UseActiveBridgeResult {
  activeBridgeId: number | null;
  activeBridge: LiveBridge | null;
  setActiveBridge: (id: number) => void;
}

export function useActiveBridge(bridges: LiveBridge[]): UseActiveBridgeResult {
  const [selectedId, setSelectedId] = useState<number | null>(() => readStored());

  // Resolve the effective active id: the explicit selection when it still
  // exists in the list (even if disconnected), otherwise the first connected
  // bridge (default). A disconnected-but-present selection is preserved.
  const present = selectedId !== null && bridges.some((b) => b.weighbridge_id === selectedId);
  const fallbackId =
    bridges.find((b) => b.agent_online)?.weighbridge_id ?? bridges[0]?.weighbridge_id ?? null;
  const activeBridgeId = present ? selectedId : fallbackId;

  // Persist the resolved default so the selection is stable across renders and
  // tabs (only when we fell back — an explicit selection is already stored).
  useEffect(() => {
    if (!present && activeBridgeId !== null && activeBridgeId !== selectedId) {
      setSelectedId(activeBridgeId);
      writeStored(activeBridgeId);
    }
  }, [present, activeBridgeId, selectedId]);

  const setActiveBridge = useCallback((id: number) => {
    setSelectedId(id);
    writeStored(id);
  }, []);

  // Camera-preview interest signalling on the active-bridge edges.
  const signalledRef = useRef<number | null>(null);
  useEffect(() => {
    if (activeBridgeId === signalledRef.current) return;
    if (signalledRef.current !== null) signalCameraPreview(signalledRef.current, false);
    if (activeBridgeId !== null) signalCameraPreview(activeBridgeId, true);
    signalledRef.current = activeBridgeId;
  }, [activeBridgeId]);

  // Release the watched bridge when the row unmounts.
  useEffect(
    () => () => {
      if (signalledRef.current !== null) {
        signalCameraPreview(signalledRef.current, false);
        signalledRef.current = null;
      }
    },
    [],
  );

  const activeBridge = bridges.find((b) => b.weighbridge_id === activeBridgeId) ?? null;
  return { activeBridgeId, activeBridge, setActiveBridge };
}
